export const FINGER_ROLE_NAMES = [
  'Thumb',
  'Index',
  'Middle',
  'Ring',
  'Pinky',
] as const;

export const NON_THUMB_FINGER_ROLE_NAMES = [
  'Index',
  'Middle',
  'Ring',
  'Pinky',
] as const;

export type SideConventionPattern = [string, string, string];

function toPascalCaseToken(token: string): string {
  return token
    .split(/[_\-. ]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

const SIDE_HINT_SEPARATORS = ['_', '-', '.'] as const;
type SideHintCaseMode = 'identity' | 'lower' | 'pascal';

interface LocalizedSideHintConfig {
  leftAffixes: readonly string[];
  rightAffixes: readonly string[];
  separatedTokenCase: SideHintCaseMode;
  joinedTokenCase: SideHintCaseMode;
  leftSuffixes?: readonly string[];
  rightSuffixes?: readonly string[];
}

const LOCALIZED_SIDE_HINT_CONFIG = {
  es: {
    leftAffixes: ['izq'],
    rightAffixes: ['der', 'derecho'],
    separatedTokenCase: 'lower',
    joinedTokenCase: 'pascal',
  },
  fr: {
    leftAffixes: ['gauche'],
    rightAffixes: ['droite'],
    separatedTokenCase: 'lower',
    joinedTokenCase: 'pascal',
  },
  jp: {
    leftAffixes: ['左'],
    rightAffixes: ['右'],
    separatedTokenCase: 'identity',
    joinedTokenCase: 'identity',
    leftSuffixes: ['.l', '_l'],
    rightSuffixes: ['.r', '_r'],
  },
} as const satisfies Record<string, LocalizedSideHintConfig>;

type SideHintLanguage = keyof typeof LOCALIZED_SIDE_HINT_CONFIG;

function formatSideHintToken(token: string, mode: SideHintCaseMode): string {
  switch (mode) {
    case 'lower':
      return token.toLowerCase();
    case 'pascal':
      return toPascalCaseToken(token);
    default:
      return token;
  }
}

function addSeparatedSideHints(
  target: Set<string>,
  token: string,
  affixes: readonly string[],
): void {
  for (const sep of SIDE_HINT_SEPARATORS) {
    for (const affix of affixes) {
      target.add(`${token}${sep}${affix}`);
      target.add(`${affix}${sep}${token}`);
    }
  }
}

function addJoinedSideHints(
  target: Set<string>,
  token: string,
  affixes: readonly string[],
): void {
  for (const affix of affixes) {
    const pascalAffix = toPascalCaseToken(affix);
    target.add(`${token}${pascalAffix}`);
    target.add(`${pascalAffix}${token}`);
  }
}

function addSuffixSideHints(
  target: Set<string>,
  token: string,
  suffixes: readonly string[],
): void {
  for (const suffix of suffixes) {
    target.add(`${token}${suffix}`);
  }
}

function buildLocalizedSideHints(
  language: SideHintLanguage,
  ...tokens: string[]
): { left: string[]; right: string[] } {
  const config: LocalizedSideHintConfig = LOCALIZED_SIDE_HINT_CONFIG[language];
  const left = new Set<string>();
  const right = new Set<string>();

  for (const token of tokens) {
    const separatedToken = formatSideHintToken(
      token,
      config.separatedTokenCase,
    );
    const joinedToken = formatSideHintToken(token, config.joinedTokenCase);

    addSeparatedSideHints(left, separatedToken, config.leftAffixes);
    addSeparatedSideHints(right, separatedToken, config.rightAffixes);
    addJoinedSideHints(left, joinedToken, config.leftAffixes);
    addJoinedSideHints(right, joinedToken, config.rightAffixes);
    addSuffixSideHints(left, token, config.leftSuffixes ?? []);
    addSuffixSideHints(right, token, config.rightSuffixes ?? []);
  }

  return {
    left: [...left],
    right: [...right],
  };
}

function buildSpanishSideHints(
  ...tokens: string[]
): { left: string[]; right: string[] } {
  return buildLocalizedSideHints('es', ...tokens);
}

function buildFrenchSideHints(
  ...tokens: string[]
): { left: string[]; right: string[] } {
  return buildLocalizedSideHints('fr', ...tokens);
}

function buildJapaneseSideHints(
  ...tokens: string[]
): { left: string[]; right: string[] } {
  return buildLocalizedSideHints('jp', ...tokens);
}

export function caseAwarePatternMatch(value: string, pattern: string): boolean {
  if (pattern === pattern.toLowerCase()) {
    return value.toLowerCase().includes(pattern);
  }
  return value.includes(pattern);
}

function uniqueStrings(values: Iterable<string>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }

  return result;
}

function hasAnyPattern(name: string, patterns: readonly string[]): boolean {
  const lower = name.toLowerCase();
  return patterns.some((pattern) => lower.includes(pattern.toLowerCase()));
}

const CONTAINER_NAME_PATTERNS = ['rootnode', 'scene', 'armature', 'skeleton'] as const;
const CONTAINER_NAME_PREFIX_PATTERNS = ['skm_', 'rig_', 'mesh_', 'char_'] as const;
const PRIMARY_ROOT_NAME_PATTERNS = ['ground', 'origin', 'base', 'main', 'global'] as const;
const SECONDARY_ROOT_NAME_PATTERNS = [
  'hip',
  'pelvis',
  'waist',
  'cadera',
  'hanche',
  'bassin',
  '腰',
  '下半身',
] as const;

export function isContainerLikeName(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    CONTAINER_NAME_PATTERNS.some((pattern) => lower.includes(pattern)) ||
    CONTAINER_NAME_PREFIX_PATTERNS.some((pattern) => lower.startsWith(pattern))
  );
}

export function isAnimationRootLikeName(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.includes('root') && !isContainerLikeName(lower);
}

export function isPrimaryRootLikeName(name: string): boolean {
  return hasAnyPattern(name, PRIMARY_ROOT_NAME_PATTERNS);
}

export function isSecondaryRootLikeName(name: string): boolean {
  return hasAnyPattern(name, SECONDARY_ROOT_NAME_PATTERNS);
}

type SideHintSet = {
  left: string[];
  right: string[];
};

type VocabularyBucket = 'en' | 'es' | 'fr' | 'jp' | 'aliases';

type LocalizedTerms = Partial<Record<VocabularyBucket, readonly string[]>>;

const VOCABULARY_BUCKET_ORDER: readonly VocabularyBucket[] = [
  'en',
  'es',
  'fr',
  'jp',
  'aliases',
];

function flattenLocalizedTerms(terms: LocalizedTerms | undefined): string[] {
  if (!terms) return [];
  return uniqueStrings(
    VOCABULARY_BUCKET_ORDER.flatMap((bucket) => terms[bucket] ?? []),
  );
}

interface AppendageDefinition {
  termsByLanguage: LocalizedTerms;
}

const APPENDAGE_DEFINITIONS = {
  Tail: {
    termsByLanguage: {
      en: ['tail'],
      es: ['cola'],
      fr: ['queue'],
      jp: ['尻尾', '尾'],
      aliases: ['tall'],
    },
  },
  Tentacle: {
    termsByLanguage: {
      en: ['tentacle'],
      es: ['tentaculo', 'tentáculo'],
      fr: ['tentacule'],
      jp: ['触手'],
    },
  },
  Antennae: {
    termsByLanguage: {
      en: ['antennae', 'antenna'],
      es: ['antena'],
      fr: ['antenne'],
      jp: ['触角'],
    },
  },
} as const satisfies Record<string, AppendageDefinition>;

export type AppendageType = keyof typeof APPENDAGE_DEFINITIONS;

export const APPENDAGE_HINTS: Record<AppendageType, string[]> = Object.fromEntries(
  Object.entries(APPENDAGE_DEFINITIONS).map(([appendageType, definition]) => [
    appendageType,
    flattenLocalizedTerms(definition.termsByLanguage),
  ]),
) as Record<AppendageType, string[]>;

interface RoleDefinitionBase {
  termsByLanguage: LocalizedTerms;
}

interface RoleDefinition extends RoleDefinitionBase {
  hintsByLanguage?: LocalizedTerms;
  extraHints?: readonly string[];
}

interface SidedRoleDefinition extends RoleDefinitionBase {
  leftHints: readonly string[];
  rightHints: readonly string[];
  generatedSideHints?: readonly SideHintSet[];
}

function buildSidedRoleHints(definition: SidedRoleDefinition): SideHintSet {
  const generatedLeft: string[] = [];
  const generatedRight: string[] = [];

  for (const hintSet of definition.generatedSideHints ?? []) {
    generatedLeft.push(...hintSet.left);
    generatedRight.push(...hintSet.right);
  }

  return {
    left: uniqueStrings([...definition.leftHints, ...generatedLeft]),
    right: uniqueStrings([...definition.rightHints, ...generatedRight]),
  };
}

const BASE_ROLE_DEFINITIONS: Record<string, RoleDefinition> = {
  Root: {
    termsByLanguage: {
      en: ['root', 'origin', 'reference', 'armature'],
      es: ['raiz', 'raíz'],
      fr: ['racine'],
      jp: ['全ての親', 'センター', 'グルーブ'],
    },
  },
  Pelvis: {
    termsByLanguage: {
      en: ['pelvis', 'hip', 'hips', 'crotch'],
      es: ['cadera'],
      fr: ['hanche', 'bassin'],
      jp: ['腰', '下半身'],
    },
    hintsByLanguage: {
      en: ['pelvis', 'hip', 'hips'],
      es: ['cadera'],
      fr: ['hanche', 'bassin'],
      jp: ['腰', '下半身'],
    },
    extraHints: ['spine_0', 'c_spine_0'],
  },
  Spine: {
    termsByLanguage: {
      en: ['spine', 'back'],
      es: ['espina'],
      fr: ['colonne', 'vertébrale', 'vertebrale'],
      jp: ['上半身'],
      aliases: ['spn'],
    },
  },
  Chest: {
    termsByLanguage: {
      en: ['chest', 'ribcage'],
      es: ['pecho'],
      fr: ['poitrine'],
      jp: ['上半身2'],
      aliases: ['upper_spine'],
    },
    hintsByLanguage: {
      en: ['chest'],
      es: ['pecho'],
      fr: ['poitrine'],
      jp: ['上半身2'],
      aliases: ['spine_03', 'spine_04', 'upper_spine'],
    },
  },
  Neck: {
    termsByLanguage: {
      en: ['neck'],
      es: ['cuello'],
      fr: ['cou'],
      jp: ['首'],
    },
  },
  Head: {
    termsByLanguage: {
      en: ['head', 'skull', 'cranium'],
      es: ['cabeza'],
      fr: ['tête', 'tete'],
      jp: ['頭'],
    },
    hintsByLanguage: {
      en: ['head', 'skull'],
      es: ['cabeza'],
      fr: ['tête', 'tete'],
      jp: ['頭'],
    },
  },
  Thumb: {
    termsByLanguage: {
      en: ['thumb'],
      es: ['pulgar'],
      fr: ['pouce'],
      jp: ['親指'],
    },
  },
  Index: {
    termsByLanguage: {
      en: ['index', 'pointer'],
      es: ['indice', 'índice'],
      fr: ['index'],
      jp: ['人差し指'],
    },
  },
  Middle: {
    termsByLanguage: {
      en: ['middle'],
      es: ['medio'],
      fr: ['majeur'],
      jp: ['中指'],
    },
  },
  Ring: {
    termsByLanguage: {
      en: ['ring'],
      es: ['anular'],
      fr: ['annulaire'],
      jp: ['薬指'],
    },
  },
  Pinky: {
    termsByLanguage: {
      en: ['pinky', 'little'],
      es: ['menique', 'meñique'],
      fr: ['auriculaire'],
      jp: ['小指'],
    },
  },
};

const SIDED_ROLE_DEFINITIONS: Record<string, SidedRoleDefinition> = {
  Clavicle: {
    termsByLanguage: {
      en: ['clavicle', 'clav', 'collar', 'shoulder'],
      es: ['hombro', 'clavical', 'clavicula'],
      fr: ['épaule', 'epaule'],
      jp: ['肩'],
    },
    leftHints: ['clavicle_l', 'clav_l', 'shoulder_l', 'collar_l', 'l_clavicle', 'l_shoulder', 'leftshoulder', 'left_shoulder', 'leftclavicle', 'left_clavicle'],
    rightHints: ['clavicle_r', 'clav_r', 'shoulder_r', 'collar_r', 'r_clavicle', 'r_shoulder', 'rightshoulder', 'right_shoulder', 'rightclavicle', 'right_clavicle'],
    generatedSideHints: [
      buildSpanishSideHints('hombro', 'clavical', 'clavicula'),
      buildFrenchSideHints('epaule', 'épaule'),
      buildJapaneseSideHints('肩p', '肩', '肩c'),
    ],
  },
  UpperArm: {
    termsByLanguage: {
      en: ['upperarm', 'uparm', 'arm', 'humerus', 'bicep'],
      es: ['brazo'],
      fr: ['bras'],
      jp: ['腕'],
    },
    leftHints: ['upperarm_l', 'uparm_l', 'arm_l', 'l_upperarm', 'l_arm', 'humerus_l', 'leftarm', 'left_arm'],
    rightHints: ['upperarm_r', 'uparm_r', 'arm_r', 'r_upperarm', 'r_arm', 'humerus_r', 'rightarm', 'right_arm'],
    generatedSideHints: [
      buildSpanishSideHints('brazo'),
      buildFrenchSideHints('bras'),
      buildJapaneseSideHints('腕'),
    ],
  },
  LowerArm: {
    termsByLanguage: {
      en: ['forearm', 'lowerarm', 'loarm', 'radius', 'elbow'],
      es: ['antebrazo'],
      fr: ['avant-bras', 'avantbras', 'avant_bras'],
      jp: ['ひじ'],
    },
    leftHints: ['lowerarm_l', 'forearm_l', 'loarm_l', 'l_forearm', 'l_lowerarm', 'radius_l', 'leftforearm', 'left_forearm', 'elbow_l', 'l_elbow', 'leftelbow', 'left_elbow'],
    rightHints: ['lowerarm_r', 'forearm_r', 'loarm_r', 'r_forearm', 'r_lowerarm', 'radius_r', 'rightforearm', 'right_forearm', 'elbow_r', 'r_elbow', 'rightelbow', 'right_elbow'],
    generatedSideHints: [
      buildSpanishSideHints('antebrazo'),
      buildFrenchSideHints('avant-bras', 'avantbras', 'avant_bras'),
      buildJapaneseSideHints('ひじ'),
    ],
  },
  Hand: {
    termsByLanguage: {
      en: ['hand', 'wrist', 'palm'],
      es: ['mano'],
      fr: ['poignet'],
      jp: ['手首'],
    },
    leftHints: ['hand_l', 'wrist_l', 'wrise_l', 'l_hand', 'l_wrist', 'l_wrise', 'lefthand', 'left_hand', 'hand.l'],
    rightHints: ['hand_r', 'wrist_r', 'wrise_r', 'r_hand', 'r_wrist', 'r_wrise', 'righthand', 'right_hand', 'hand.r', 'maincannon', 'cannon'],
    generatedSideHints: [
      buildSpanishSideHints('mano'),
      buildFrenchSideHints('poignet'),
      buildJapaneseSideHints('手首'),
    ],
  },
  UpperLeg: {
    termsByLanguage: {
      en: ['thigh', 'upperleg', 'femur', 'leg upper'],
      es: ['pierna'],
      fr: ['jambe'],
      jp: ['足', '大腿'],
      aliases: ['upleg', 'leg_01', 'leg01'],
    },
    leftHints: ['thigh_l', 'upperleg_l', 'upleg_l', 'l_thigh', 'l_upperleg', 'femur_l', 'hip_l', 'l_hip', 'leftupleg', 'left_upleg', 'leg left upper', 'left_crotch'],
    rightHints: ['thigh_r', 'upperleg_r', 'upleg_r', 'r_thigh', 'r_upperleg', 'femur_r', 'hip_r', 'r_hip', 'rightupleg', 'right_upleg', 'leg right upper', 'right_crotch'],
    generatedSideHints: [
      buildSpanishSideHints('pierna'),
      buildFrenchSideHints('jambe'),
      buildJapaneseSideHints('足'),
    ],
  },
  LowerLeg: {
    termsByLanguage: {
      en: ['calf', 'lowerleg', 'shin', 'tibia', 'knee', 'leg lower'],
      es: ['rodilla'],
      fr: ['genou', 'jambe'],
      jp: ['ひざ', '下腿'],
      aliases: ['leg_02', 'leg02'],
    },
    leftHints: ['calf_l', 'lowerleg_l', 'shin_l', 'l_calf', 'l_lowerleg', 'tibia_l', 'leftleg', 'left_leg', 'knee_l', 'l_knee', 'leftknee', 'left_knee'],
    rightHints: ['calf_r', 'lowerleg_r', 'shin_r', 'r_calf', 'r_lowerleg', 'tibia_r', 'rightleg', 'right_leg', 'knee_r', 'r_knee', 'rightknee', 'right_knee'],
    generatedSideHints: [
      buildSpanishSideHints('rodilla'),
      buildFrenchSideHints('genou', 'jambe'),
      buildJapaneseSideHints('ひざ'),
    ],
  },
  Hock: {
    termsByLanguage: {
      en: ['hock', 'metatars', 'cannon', 'fetlock', 'pastern', 'midfoot'],
      es: ['corvejon', 'corvejón'],
      fr: ['jarret'],
      jp: ['飛節'],
    },
    leftHints: ['hock_l', 'l_hock', 'metatars_l', 'l_metatars', 'cannon_l', 'l_cannon', 'fetlock_l', 'l_fetlock', 'midfoot_l', 'l_midfoot'],
    rightHints: ['hock_r', 'r_hock', 'metatars_r', 'r_metatars', 'cannon_r', 'r_cannon', 'fetlock_r', 'r_fetlock', 'midfoot_r', 'r_midfoot'],
    generatedSideHints: [
      buildSpanishSideHints('corvejon', 'corvejón'),
      buildFrenchSideHints('jarret'),
      buildJapaneseSideHints('飛節'),
    ],
  },
  Foot: {
    termsByLanguage: {
      en: ['foot', 'ankle'],
      es: ['pie'],
      fr: ['pied'],
      jp: ['足首'],
    },
    leftHints: ['foot_l', 'ankle_l', 'l_foot', 'l_ankle', 'leftfoot', 'left_foot'],
    rightHints: ['foot_r', 'ankle_r', 'r_foot', 'r_ankle', 'rightfoot', 'right_foot'],
    generatedSideHints: [
      buildSpanishSideHints('pie'),
      buildFrenchSideHints('pied'),
      buildJapaneseSideHints('足首'),
    ],
  },
  Toe: {
    termsByLanguage: {
      en: ['toe', 'ball', 'toebase', 'toes'],
      es: ['dedo_pata', 'dedopata', 'dedo_pate', 'dedopate'],
      fr: ['orteil'],
      jp: ['つま先'],
    },
    leftHints: ['ball_l', 'toe_l', 'l_ball', 'l_toe', 'lefttoebase', 'left_toebase', 'leg left toes', 'left_foot_thumb', 'left_foot_index'],
    rightHints: ['ball_r', 'toe_r', 'r_ball', 'r_toe', 'righttoebase', 'right_toebase', 'leg right toes', 'right_foot_thumb', 'right_foot_index'],
    generatedSideHints: [
      buildSpanishSideHints('dedo_pata', 'dedopata'),
      buildSpanishSideHints('dedo_pate', 'dedopate'),
      buildFrenchSideHints('orteil'),
      buildJapaneseSideHints('つま先'),
    ],
  },
};

export const ROLE_TERMS: Record<string, string[]> = {
  ...Object.fromEntries(
    Object.entries(BASE_ROLE_DEFINITIONS).map(([role, definition]) => [
      role,
      flattenLocalizedTerms(definition.termsByLanguage),
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(SIDED_ROLE_DEFINITIONS).map(([role, definition]) => [
      role,
      flattenLocalizedTerms(definition.termsByLanguage),
    ]),
  ),
};

const DIRECT_ROLE_HINTS = Object.fromEntries(
  Object.entries(BASE_ROLE_DEFINITIONS).map(([role, definition]) => [
    role,
    uniqueStrings([
      ...flattenLocalizedTerms(
        definition.hintsByLanguage ?? definition.termsByLanguage,
      ),
      ...(definition.extraHints ?? []),
    ]),
  ]),
) as Record<string, string[]>;

const SIDED_ROLE_HINTS = Object.fromEntries(
  Object.entries(SIDED_ROLE_DEFINITIONS).flatMap(([role, definition]) => {
    const hints = buildSidedRoleHints(definition);
    return [
      [`Left${role}`, hints.left],
      [`Right${role}`, hints.right],
    ];
  }),
) as Record<string, string[]>;

const FINGER_ROLE_HINTS = {
  LeftThumb1: ['thumb_01_l', 'thumb_1_l', 'l_thumb_01', 'l_thumb1'],
  LeftThumb2: ['thumb_02_l', 'thumb_2_l', 'l_thumb_02', 'l_thumb2'],
  LeftThumb3: ['thumb_03_l', 'thumb_3_l', 'l_thumb_03', 'l_thumb3'],
  LeftIndexMetacarpal: [
    'index_metacarpal_l',
    'l_index_metacarpal',
    'leftindexmetacarpal',
  ],
  LeftIndex1: ['index_01_l', 'index_1_l', 'l_index_01', 'l_index1'],
  LeftIndex2: ['index_02_l', 'index_2_l', 'l_index_02', 'l_index2'],
  LeftIndex3: ['index_03_l', 'index_3_l', 'l_index_03', 'l_index3'],
  LeftMiddleMetacarpal: [
    'middle_metacarpal_l',
    'l_middle_metacarpal',
    'leftmiddlemetacarpal',
  ],
  LeftMiddle1: ['middle_01_l', 'middle_1_l', 'l_middle_01', 'l_middle1'],
  LeftMiddle2: ['middle_02_l', 'middle_2_l', 'l_middle_02', 'l_middle2'],
  LeftMiddle3: ['middle_03_l', 'middle_3_l', 'l_middle_03', 'l_middle3'],
  LeftRingMetacarpal: [
    'ring_metacarpal_l',
    'l_ring_metacarpal',
    'leftringmetacarpal',
  ],
  LeftRing1: ['ring_01_l', 'ring_1_l', 'l_ring_01', 'l_ring1'],
  LeftRing2: ['ring_02_l', 'ring_2_l', 'l_ring_02', 'l_ring2'],
  LeftRing3: ['ring_03_l', 'ring_3_l', 'l_ring_03', 'l_ring3'],
  LeftPinkyMetacarpal: [
    'pinky_metacarpal_l',
    'little_metacarpal_l',
    'l_pinky_metacarpal',
    'leftpinkymetacarpal',
  ],
  LeftPinky1: ['pinky_01_l', 'pinky_1_l', 'l_pinky_01', 'l_pinky1'],
  LeftPinky2: ['pinky_02_l', 'pinky_2_l', 'l_pinky_02', 'l_pinky2'],
  LeftPinky3: ['pinky_03_l', 'pinky_3_l', 'l_pinky_03', 'l_pinky3'],
  RightThumb1: ['thumb_01_r', 'thumb_1_r', 'r_thumb_01', 'r_thumb1'],
  RightThumb2: ['thumb_02_r', 'thumb_2_r', 'r_thumb_02', 'r_thumb2'],
  RightThumb3: ['thumb_03_r', 'thumb_3_r', 'r_thumb_03', 'r_thumb3'],
  RightIndexMetacarpal: [
    'index_metacarpal_r',
    'r_index_metacarpal',
    'rightindexmetacarpal',
  ],
  RightIndex1: ['index_01_r', 'index_1_r', 'r_index_01', 'r_index1'],
  RightIndex2: ['index_02_r', 'index_2_r', 'r_index_02', 'r_index2'],
  RightIndex3: ['index_03_r', 'index_3_r', 'r_index_03', 'r_index3'],
  RightMiddleMetacarpal: [
    'middle_metacarpal_r',
    'r_middle_metacarpal',
    'rightmiddlemetacarpal',
  ],
  RightMiddle1: ['middle_01_r', 'middle_1_r', 'r_middle_01', 'r_middle1'],
  RightMiddle2: ['middle_02_r', 'middle_2_r', 'r_middle_02', 'r_middle2'],
  RightMiddle3: ['middle_03_r', 'middle_3_r', 'r_middle_03', 'r_middle3'],
  RightRingMetacarpal: [
    'ring_metacarpal_r',
    'r_ring_metacarpal',
    'rightringmetacarpal',
  ],
  RightRing1: ['ring_01_r', 'ring_1_r', 'r_ring_01', 'r_ring1'],
  RightRing2: ['ring_02_r', 'ring_2_r', 'r_ring_02', 'r_ring2'],
  RightRing3: ['ring_03_r', 'ring_3_r', 'r_ring_03', 'r_ring3'],
  RightPinkyMetacarpal: [
    'pinky_metacarpal_r',
    'little_metacarpal_r',
    'r_pinky_metacarpal',
    'rightpinkymetacarpal',
  ],
  RightPinky1: ['pinky_01_r', 'pinky_1_r', 'r_pinky_01', 'r_pinky1'],
  RightPinky2: ['pinky_02_r', 'pinky_2_r', 'r_pinky_02', 'r_pinky2'],
  RightPinky3: ['pinky_03_r', 'pinky_3_r', 'r_pinky_03', 'r_pinky3'],
} as const satisfies Record<string, string[]>;

export const ROLE_HINTS: Record<string, string[]> = {
  ...DIRECT_ROLE_HINTS,
  ...SIDED_ROLE_HINTS,
  ...FINGER_ROLE_HINTS,
};

export const SIDE_PATTERNS: Record<string, string[]> = {
  Left: ['_l', '_l_', 'left', 'l_', '.l', '_left', 'gauche', '_izq', 'izq_', '_izq_', '-izq', 'izq-', '.izq', 'izq.', 'Izq'],
  Right: ['_r', '_r_', 'right', 'r_', '.r', '_right', 'droite', '_der', 'der_', '_der_', '-der', 'der-', '.der', 'der.', '_derecho', 'derecho_', '_derecho_', '-derecho', 'derecho-', '.derecho', 'derecho.', 'Der', 'Derecho'],
};

export const SIDE_CONVENTION_PATTERNS: SideConventionPattern[] = [
  ['-left-', '-right-', 'middle'],
  ['_left_', '_right_', 'middle'],
  ['.left.', '.right.', 'middle'],
  [' left ', ' right ', 'middle'],
  ['left_', 'right_', 'prefix'],
  ['left-', 'right-', 'prefix'],
  ['left.', 'right.', 'prefix'],
  ['_left', '_right', 'suffix'],
  ['-left', '-right', 'suffix'],
  ['.left', '.right', 'suffix'],
  ['_l_', '_r_', 'middle'],
  ['-l-', '-r-', 'middle'],
  ['.l.', '.r.', 'middle'],
  [' l ', ' r ', 'middle'],
  ['l_', 'r_', 'prefix'],
  ['l-', 'r-', 'prefix'],
  ['l.', 'r.', 'prefix'],
  ['_l', '_r', 'suffix'],
  ['-l', '-r', 'suffix'],
  ['.l', '.r', 'suffix'],
  ['_lf_', '_rt_', 'middle'],
  ['-lf-', '-rt-', 'middle'],
  ['_lt_', '_rt_', 'middle'],
  ['-lt-', '-rt-', 'middle'],
  ['lf_', 'rt_', 'prefix'],
  ['lf-', 'rt-', 'prefix'],
  ['lt_', 'rt_', 'prefix'],
  ['lt-', 'rt-', 'prefix'],
  ['_lf', '_rt', 'suffix'],
  ['-lf', '-rt', 'suffix'],
  ['_lt', '_rt', 'suffix'],
  ['-lt', '-rt', 'suffix'],
  ['.lf', '.rt', 'suffix'],
  ['.lt', '.rt', 'suffix'],
  ['.L', '.R', 'suffix'],
  ['_L', '_R', 'suffix'],
  ['-L', '-R', 'suffix'],
  ['_LEFT', '_RIGHT', 'suffix'],
  ['LEFT_', 'RIGHT_', 'prefix'],
  ['_LF', '_RT', 'suffix'],
  ['_LT', '_RT', 'suffix'],
  ['LF_', 'RT_', 'prefix'],
  ['LT_', 'RT_', 'prefix'],
  ['gauche', 'droite', 'suffix'],
  ['_izq_', '_der_', 'middle'],
  ['_izq_', '_derecho_', 'middle'],
  ['-izq-', '-der-', 'middle'],
  ['-izq-', '-derecho-', 'middle'],
  ['.izq.', '.der.', 'middle'],
  ['.izq.', '.derecho.', 'middle'],
  ['izq_', 'der_', 'prefix'],
  ['izq_', 'derecho_', 'prefix'],
  ['izq-', 'der-', 'prefix'],
  ['izq-', 'derecho-', 'prefix'],
  ['izq.', 'der.', 'prefix'],
  ['izq.', 'derecho.', 'prefix'],
  ['_izq', '_der', 'suffix'],
  ['_izq', '_derecho', 'suffix'],
  ['-izq', '-der', 'suffix'],
  ['-izq', '-derecho', 'suffix'],
  ['.izq', '.der', 'suffix'],
  ['.izq', '.derecho', 'suffix'],
  ['Izq', 'Der', 'prefix'],
  ['Izq', 'Der', 'suffix'],
  ['Izq', 'Derecho', 'prefix'],
  ['Izq', 'Derecho', 'suffix'],
  ['.l', '.r', 'suffix'],
  ['左', '右', 'suffix'],
  ['_左', '_右', 'suffix'],
  ['left', 'right', 'prefix'],
  ['Left', 'Right', 'prefix'],
];

export interface SideConvention {
  leftPatterns: string[];
  rightPatterns: string[];
  position: string;
  matchCount: number;
  confidence: number;
}

export function collectRoleTerms(roles: readonly string[]): string[] {
  return uniqueStrings(roles.flatMap((role) => ROLE_TERMS[role] ?? []));
}

export function hasRoleTerm(name: string, role: string): boolean {
  return hasAnyPattern(name, ROLE_TERMS[role] ?? []);
}

export function nameMatchesAnyRole(name: string, roles: readonly string[]): boolean {
  return roles.some((role) => hasRoleTerm(name, role));
}

export function isPelvisLikeName(name: string): boolean {
  return hasRoleTerm(name, 'Pelvis');
}

export function isSpineLikeName(name: string): boolean {
  return nameMatchesAnyRole(name, ['Spine', 'Chest']);
}

export function isNeckLikeName(name: string): boolean {
  return hasRoleTerm(name, 'Neck');
}

export function isHeadLikeName(name: string): boolean {
  return hasRoleTerm(name, 'Head');
}

export function isClavicleLikeName(name: string): boolean {
  return hasRoleTerm(name, 'Clavicle');
}

export function isUpperArmLikeName(name: string): boolean {
  return hasRoleTerm(name, 'UpperArm');
}

export function isLowerArmLikeName(name: string): boolean {
  return hasRoleTerm(name, 'LowerArm');
}

export function isArmLikeName(name: string): boolean {
  return nameMatchesAnyRole(name, ['UpperArm', 'LowerArm', 'Clavicle']);
}

export function isHandLikeName(name: string): boolean {
  return hasRoleTerm(name, 'Hand');
}

const METACARPAL_NAME_PATTERNS = ['metacarpal', 'carpal'] as const;
const FINGER_HELPER_CONTEXT_PATTERNS = [
  'finger',
  'thumb',
  'index',
  'middle',
  'mid',
  'ring',
  'pinky',
  'little',
  '親指',
  '人指',
  '中指',
  '薬指',
  '小指',
  'doigt',
] as const;
const FINGER_HELPER_NAME_PATTERNS = ['cup', 'frzgrp', 'zerofrzgrp'] as const;

export function isMetacarpalLikeName(name: string): boolean {
  return hasAnyPattern(name, METACARPAL_NAME_PATTERNS);
}

export function isFingerHelperLikeName(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    hasAnyPattern(lower, FINGER_HELPER_CONTEXT_PATTERNS) &&
    hasAnyPattern(lower, FINGER_HELPER_NAME_PATTERNS)
  );
}

export function isUpperLegLikeName(name: string): boolean {
  return hasRoleTerm(name, 'UpperLeg');
}

export function isLowerLegLikeName(name: string): boolean {
  return hasRoleTerm(name, 'LowerLeg');
}

export function isLegLikeName(name: string): boolean {
  return (
    nameMatchesAnyRole(name, ['UpperLeg', 'LowerLeg', 'Foot', 'Toe']) ||
    hasAnyPattern(name, ['leg'])
  );
}

export function isHockLikeName(name: string): boolean {
  return hasRoleTerm(name, 'Hock');
}

export function isFootLikeName(name: string): boolean {
  return hasRoleTerm(name, 'Foot');
}

export function isToeLikeName(name: string): boolean {
  return hasRoleTerm(name, 'Toe');
}

export function isToeRoleCompatibleName(name: string): boolean {
  return isToeLikeName(name) || hasAnyPattern(name, ['foot_thumb', 'foot_index']);
}

export function isInlineDeformationLikeName(name: string): boolean {
  return hasAnyPattern(name, ['twist', 'roll']);
}

export function isTwistLikeName(name: string): boolean {
  return hasAnyPattern(name, ['twist']);
}

export function isTerminalHelperLikeName(name: string): boolean {
  return hasAnyPattern(name, ['detach', 'end']);
}

export function isGenericNumberedPlaceholderJointName(name: string): boolean {
  const normalized = name
    .toLowerCase()
    .split(/[\\/]/u)
    .at(-1)
    ?.trim()
    .replace(/^[_:|\s.-]+/u, '') ?? '';

  return /^joint[_-]?\d+$/u.test(normalized);
}

export function isTorsoAuxiliaryLeafName(name: string): boolean {
  return (
    isTerminalHelperLikeName(name) ||
    hasAnyPattern(name, ['plate', 'fx', 'ik'])
  );
}

export function isAccessoryBranchLikeName(name: string): boolean {
  return hasAnyPattern(name, [
    'flap',
    'thruster',
    'tube',
    'pad',
    'piston',
    'prop',
    'buster',
    'vine',
    'crystal',
    'discus',
    'awl',
  ]);
}

export function isStructuralArmAuxiliaryName(name: string): boolean {
  if (isTerminalHelperLikeName(name)) return false;
  if (isLowerArmLikeName(name)) return true;
  return (
    isInlineDeformationLikeName(name) &&
    (isArmLikeName(name) || name.toLowerCase().includes('shoulder'))
  );
}

export function isStructuralLegAuxiliaryName(name: string): boolean {
  if (isTerminalHelperLikeName(name)) return false;
  return isInlineDeformationLikeName(name) && (isLegLikeName(name) || isPelvisLikeName(name));
}

export function isSideHipConnectorName(name: string): boolean {
  return isPelvisLikeName(name) && !isTwistLikeName(name);
}

export function isLegHipChainName(name: string): boolean {
  return isUpperLegLikeName(name) || (isLegLikeName(name) && isPelvisLikeName(name));
}

export function isUpperLegChainCandidateName(name: string): boolean {
  return (
    isLegHipChainName(name) ||
    hasAnyPattern(name, ['leg_1']) ||
    (isPelvisLikeName(name) && isTwistLikeName(name))
  );
}

export function isStructuralArmChainName(name: string): boolean {
  return isArmLikeName(name) || isHandLikeName(name);
}

export function isInlineChestLikeName(name: string): boolean {
  return hasAnyPattern(name, ['spinechest', 'spine_chest', 'upperchest', 'upper_chest']);
}

export function isPrimarySpineBridgeName(name: string): boolean {
  return isSpineLikeName(name) && hasAnyPattern(name, ['first']);
}

export function isSimpleHeadSegmentName(name: string): boolean {
  const normalized = name
    .toLowerCase()
    .replace(/(_env|_jnt|-env|-jnt)$/u, '');
  return /^head(?:[_-]?\d+)?$/u.test(normalized);
}

export function isHeadDecorativeName(name: string): boolean {
  return hasAnyPattern(name, [
    'jaw',
    'eye',
    'lid',
    'brow',
    'face',
    'muzzle',
    'mouth',
    'nose',
    'unused',
    'antenna',
    'aim',
  ]) || isTerminalHelperLikeName(name);
}

export function isDeformationHelperLikeName(name: string): boolean {
  const lower = name.toLowerCase();
  const hasHelperOrdinal = /(?:^|[_\-.])\d+(?:[_\-.]|$)/u.test(lower) || /[a-z]\d+[_\-.]?/u.test(lower);
  const isPlateHelper =
    /(?:^|[_\-.])plate(?:[_\-.]|$)/u.test(lower) ||
    /[a-z]plate(?:[_\-.]|$)/u.test(lower);
  const isSoftTissueHelper =
    lower.includes('fat') ||
    lower.startsWith('fintail') ||
    lower.startsWith('wingskin') ||
    /^(?:belly|chest)[a-z]+\d/u.test(lower);
  return (
    hasAnyPattern(lower, ['deform']) ||
    isSoftTissueHelper ||
    isPlateHelper ||
    hasAnyPattern(lower, ['tweaker', 'stretch']) ||
    (hasHelperOrdinal &&
      hasAnyPattern(lower, [
        'biceps',
        'chestmuscle',
        'chest_muscle',
        'trapezius',
        'trapezious',
      ]))
  );
}

export function classifyToeParentName(
  name: string,
): 'foot' | 'leg' | 'toe' | 'unknown' {
  if (isFootLikeName(name) && !isToeLikeName(name)) return 'foot';
  if (isToeLikeName(name)) return 'toe';
  if (isUpperLegLikeName(name) || isLowerLegLikeName(name) || isLegLikeName(name))
    return 'leg';
  return 'unknown';
}

function matchesAppendageHint(name: string, hint: string): boolean {
  const lower = name.toLowerCase();
  const hintLower = hint.toLowerCase();

  if (lower.startsWith(hintLower)) return true;

  for (let i = 1; i <= name.length - hintLower.length; i += 1) {
    if (lower.substring(i, i + hintLower.length) !== hintLower) continue;

    const prev = name[i - 1];
    const cur = name[i];
    const startsAfterSeparator =
      prev === '_' || prev === '-' || prev === '.' || (prev >= '0' && prev <= '9');
    const startsAtCamelBoundary =
      prev >= 'a' && prev <= 'z' && cur >= 'A' && cur <= 'Z';

    if (startsAfterSeparator || startsAtCamelBoundary) return true;
  }

  return false;
}

export function matchAppendageTypeFromName(name: string): AppendageType | null {
  for (const [appendageType, hints] of Object.entries(APPENDAGE_HINTS)) {
    if (hints.some((hint) => matchesAppendageHint(name, hint))) {
      return appendageType as AppendageType;
    }
  }
  return null;
}

const SEMANTIC_NAMING_EXTRA_PATTERNS = [
  'leg',
  'finger',
  'dedo',
  'doigt',
  'lowleg',
  'lowarm',
  'cola',
  'queue',
  'lengua',
  'langue',
  'ojo',
  'oeil',
  'œil',
  'mandivula',
  'mandibula',
  'mâchoire',
  'machoire',
  '人指',
] as const;

export function getSemanticNamingPatterns(): string[] {
  return uniqueStrings([
    ...Object.values(ROLE_TERMS).flat(),
    ...SEMANTIC_NAMING_EXTRA_PATTERNS,
  ]);
}

function detectLocalizedBareSideSuffix(boneName: string): string | null {
  if (!/[^\x00-\x7F]/.test(boneName)) return null;
  if (boneName.endsWith('L')) return 'Left';
  if (boneName.endsWith('R')) return 'Right';
  return null;
}

export function detectSideFromName(boneName: string): string | null {
  for (const [side, patterns] of Object.entries(SIDE_PATTERNS)) {
    for (const pattern of patterns) {
      if (caseAwarePatternMatch(boneName, pattern)) return side;
    }
  }
  const localizedSuffixSide = detectLocalizedBareSideSuffix(boneName);
  if (localizedSuffixSide !== null) return localizedSuffixSide;
  return null;
}

export function sideConventionUnknown(): SideConvention {
  return {
    leftPatterns: [],
    rightPatterns: [],
    position: 'unknown',
    matchCount: 0,
    confidence: 0,
  };
}

export function sideConventionIsValid(c: SideConvention): boolean {
  return c.confidence > 0 && c.leftPatterns.length > 0;
}

export function sideConventionDescribe(c: SideConvention): string {
  if (!sideConventionIsValid(c)) return 'No side convention detected';
  const left = c.leftPatterns[0] ?? '?';
  const right = c.rightPatterns[0] ?? '?';
  return `${left} / ${right} (${c.position}, ${c.matchCount} matches)`;
}

export function sideConventionDetectSide(
  c: SideConvention,
  boneName: string,
): string | null {
  if (c.leftPatterns.length > 0 && c.rightPatterns.length > 0) {
    for (const pattern of c.leftPatterns) {
      if (caseAwarePatternMatch(boneName, pattern)) return 'Left';
    }
    for (const pattern of c.rightPatterns) {
      if (caseAwarePatternMatch(boneName, pattern)) return 'Right';
    }
  }

  for (const [leftPat, rightPat] of SIDE_CONVENTION_PATTERNS) {
    if (caseAwarePatternMatch(boneName, leftPat)) return 'Left';
    if (caseAwarePatternMatch(boneName, rightPat)) return 'Right';
  }

  return null;
}

export function detectSideConvention(boneNames: string[]): SideConvention {
  if (boneNames.length === 0) return sideConventionUnknown();

  const counts = new Map<string, number>();
  for (const name of boneNames) {
    for (const [leftPat, rightPat, position] of SIDE_CONVENTION_PATTERNS) {
      if (
        caseAwarePatternMatch(name, leftPat) ||
        caseAwarePatternMatch(name, rightPat)
      ) {
        const key = `${leftPat}\0${rightPat}\0${position}`;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
  }

  if (counts.size === 0) return sideConventionUnknown();

  let bestKey = '';
  let bestCount = 0;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      bestKey = key;
    }
  }

  const [leftPat, rightPat, position] = bestKey.split('\0');
  const patternLength = leftPat.length;
  const lengthBonus = Math.min(0.3, patternLength * 0.05);
  const countScore = Math.min(1.0, bestCount / 10.0);
  const confidence = Math.min(1.0, countScore + lengthBonus);

  return {
    leftPatterns: [leftPat],
    rightPatterns: [rightPat],
    position,
    matchCount: bestCount,
    confidence,
  };
}

export function detectSideFromNameWithConvention(
  boneName: string,
  convention?: SideConvention | null,
): string | null {
  if (convention && sideConventionIsValid(convention)) {
    const sideFromConvention = sideConventionDetectSide(convention, boneName);
    if (sideFromConvention !== null) return sideFromConvention;
  }
  return detectSideFromName(boneName);
}
